import { RESYNCABLE_DATA_SOURCES } from '@/features/dataSources/components/dataSourceDetails/components/tabs/constants';
import { resetUploadStatus } from '@/features/fileUploading/fileUploadingSlice';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { Button } from '@/lib';
import { DataSourceIDEnum } from '@/models/dataSource';
import { IModalContentProps } from '@/models/modals';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useResyncDataSourceMutation, useUploadDataSourceFileMutation } from '@/services/dataSources';
import axios from 'axios';
import { DateTime } from 'luxon';
import { ChangeEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuFileSpreadsheet } from 'react-icons/lu';
import { ComuniList, useComuniLists } from '../useComuniLists';
import { CitizensCount } from './CitizensCount';
import { ColumnMismatchAlert } from './ColumnMismatchAlert';
import { ColumnMismatch, parseColumnMismatchMessage } from './parseColumnMismatchMessage';

/**
 * Updatable lists: single-datasource uploads whose datasource is plain CSV.
 * Encrypted CSV is excluded even though it also resyncs as 'csv' server-side: this modal always
 * uploads unencrypted (resetUploadStatus() -> encrypted:false) with no client-side encryption step.
 */
export const isUpdatableList = (list: ComuniList): boolean =>
  list.isUpload &&
  list.datasources.length === 1 &&
  list.datasources[0].type.id === DataSourceIDEnum.CsvFile &&
  RESYNCABLE_DATA_SOURCES[list.datasources[0].type.id as DataSourceIDEnum] === 'csv';

type UnwrapError = { status?: number; data?: { message?: string } };

/** The BFF refuses a resync while one is already running (409). */
export const isConflictError = (error: unknown): boolean => (error as UnwrapError | undefined)?.status === 409;

export const extractServerMessage = (error: unknown): string | undefined => (error as UnwrapError | undefined)?.data?.message;

type UpdateListModalProps = { onDone?: () => void; onUploadAsNew?: () => void } & IModalContentProps;

/** Replace a list's data via a new CSV extraction: upload the file, then resync the datasource in place. */
export const UpdateListModal: FC<UpdateListModalProps> = ({ onDone, onUploadAsNew, closeModal }) => {
  const { t } = useTranslation('comuni');
  const { id: workspaceId } = useWorkspace();
  const dispatch = useAppDispatch();
  const { lists } = useComuniLists();
  const [uploadFile] = useUploadDataSourceFileMutation();
  const [resync] = useResyncDataSourceMutation();

  const [selectedList, setSelectedList] = useState<ComuniList | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mismatch, setMismatch] = useState<ColumnMismatch | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const updatableLists = lists.filter(isUpdatableList);

  const clearErrors = () => {
    setErrorMessage(null);
    setMismatch(null);
  };

  const pickList = (list: ComuniList) => {
    setSelectedList(list);
    setFile(null);
    setFileInputKey((k) => k + 1);
    clearErrors();
  };

  const chooseAnotherFile = () => {
    setFile(null);
    setFileInputKey((k) => k + 1);
    clearErrors();
  };

  const handleUploadAsNew = () => {
    closeModal();
    onUploadAsNew?.();
  };

  const handleConfirm = async () => {
    if (!file || !selectedList) return;
    setSubmitting(true);
    clearErrors();
    dispatch(resetUploadStatus());
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploaded = await uploadFile({ file: formData, workspaceId, cancelTokenSource: axios.CancelToken.source() }).unwrap();
      await resync({
        workspaceId,
        dataSourceId: selectedList.datasources[0].id,
        type: 'csv',
        url: uploaded.path,
        evalType: 'purgeEvalAudienceNotSync',
      }).unwrap();
      dispatch(showToast({ type: ToastType.SUCCESS, title: t('import.updateSuccess') }));
      onDone?.();
      closeModal();
    } catch (err) {
      if (isConflictError(err)) {
        setErrorMessage(t('import.updateConflict'));
      } else {
        const serverMessage = extractServerMessage(err);
        const parsed = parseColumnMismatchMessage(serverMessage);
        if (parsed) {
          setMismatch(parsed);
        } else {
          setErrorMessage(serverMessage ?? t('import.updateError'));
        }
      }
      setSubmitting(false);
    }
  };

  return (
    <div className='flex w-full max-w-xl flex-col gap-5 p-8'>
      <h3 className='text-lg font-semibold text-primary-900'>{t('import.updatePick')}</h3>

      {!selectedList ? (
        <div className='flex max-h-[50vh] flex-col gap-2 overflow-y-auto'>
          {updatableLists.length === 0 && <p className='text-sm text-quaternary-400'>{t('lists.empty')}</p>}
          {updatableLists.map((list) => (
            <button
              key={list.id}
              type='button'
              onClick={() => pickList(list)}
              className='flex items-center justify-between gap-4 rounded-xl border px-4 py-3 text-left hover:border-brand-400'
            >
              <span className='flex min-w-0 items-center gap-3'>
                <LuFileSpreadsheet className='size-6 flex-none text-brand-700' />
                <span className='truncate font-semibold text-primary-900'>{list.name}</span>
              </span>
              <span className='flex flex-none items-center gap-3 text-sm text-tertiary-600'>
                <CitizensCount datasourceId={list.datasources[0].id} />
                <span>{DateTime.fromISO(list.createdOn).toFormat('dd/LL/yyyy')}</span>
              </span>
            </button>
          ))}
          <div className='mt-2 flex justify-end'>
            <Button intent='secondary' size='md' text={t('lists.close')} onClick={closeModal} />
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-4'>
          <button
            type='button'
            disabled={submitting}
            onClick={() => {
              setSelectedList(null);
              setFile(null);
              setFileInputKey((k) => k + 1);
              clearErrors();
            }}
            className='self-start text-sm font-semibold text-brand-700 disabled:opacity-50'
          >
            &lsaquo; {t('import.updatePick')}
          </button>
          <p className='rounded-lg bg-secondary-background px-4 py-3 text-sm text-tertiary-600'>
            {t('import.updateReplaceWarning', { name: selectedList.name })}
          </p>
          <input
            key={fileInputKey}
            type='file'
            accept='.csv'
            disabled={submitting}
            onChange={(ev: ChangeEvent<HTMLInputElement>) => setFile(ev.target.files?.[0] ?? null)}
            className='text-sm text-tertiary-600'
          />
          {mismatch ? (
            <ColumnMismatchAlert listName={selectedList.name} missing={mismatch.missing} unexpected={mismatch.unexpected} />
          ) : null}
          {errorMessage && !mismatch ? (
            <p role='alert' className='rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600'>
              {errorMessage}
            </p>
          ) : null}
          <div className='flex flex-wrap justify-end gap-3'>
            <Button intent='secondary' size='md' text={t('lists.close')} onClick={closeModal} disabled={submitting} />
            {mismatch ? (
              <>
                <Button
                  intent='secondary'
                  size='md'
                  text={t('import.chooseAnotherFile')}
                  onClick={chooseAnotherFile}
                  disabled={submitting}
                />
                <Button
                  intent='primary'
                  size='md'
                  text={t('import.uploadAsNewList')}
                  onClick={handleUploadAsNew}
                  disabled={submitting}
                />
              </>
            ) : (
              <Button
                intent='primary'
                size='md'
                text={t('import.updateConfirm')}
                onClick={handleConfirm}
                disabled={!file}
                isLoading={submitting}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
